import { useEffect, useRef, useState } from 'react';
import { getAuthToken } from '@/utils/fetchWrapper';
import { getWebSocketBaseUrl } from '@/config/app';

const RECONNECT_DELAY_MS = 3_000;
const PING_INTERVAL_MS = 30000;
const MAX_RECONNECT_ATTEMPTS = 5;

export function useAuthenticatedWebSocket(
  enabled: boolean,
  path: string,
  onMessage: (data: unknown) => void,
  logLabel: string
) {
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const onMessageRef = useRef(onMessage);

  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    if (!enabled) {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      setIsConnected(false);
      return;
    }

    const cleanupConnection = () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };

    const connect = () => {
      try {
        const authToken = getAuthToken();
        if (!authToken) {
          setIsConnected(false);
          if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
            reconnectAttempts.current += 1;
            const delay = RECONNECT_DELAY_MS * reconnectAttempts.current;
            reconnectTimeoutRef.current = setTimeout(connect, delay);
          }
          return;
        }

        const wsUrl = `${getWebSocketBaseUrl()}${path}?token=${encodeURIComponent(authToken)}`;
        const ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          setIsConnected(true);
          reconnectAttempts.current = 0;
          pingIntervalRef.current = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'ping' }));
            }
          }, PING_INTERVAL_MS);
        };

        ws.onmessage = event => {
          try {
            onMessageRef.current(JSON.parse(event.data));
          } catch (e) {
            console.error(`[${logLabel}] Error parsing message:`, e);
          }
        };

        ws.onerror = errorEvent => {
          console.error(`[${logLabel}] Error:`, errorEvent);
          setIsConnected(false);
        };

        ws.onclose = event => {
          setIsConnected(false);
          if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
          }

          if (
            event.code !== 1000 &&
            reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS
          ) {
            reconnectAttempts.current += 1;
            const delay = RECONNECT_DELAY_MS * reconnectAttempts.current;
            reconnectTimeoutRef.current = setTimeout(() => {
              connect();
            }, delay);
          }
        };

        wsRef.current = ws;
      } catch (e) {
        console.error(`[${logLabel}] Connection error:`, e);
        setIsConnected(false);
      }
    };

    connect();

    return cleanupConnection;
  }, [enabled, path, logLabel]);

  return { isConnected };
}
